import { acss } from '@adornis/chemistry/directives/acss.js';
import type { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import '@adornis/internationalization/client/language-picker/x-country-picker.js';
import '@adornis/internationalization/client/x-tel-input.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import type { BHTranslationDictionary } from '../../translations/translation.js';
import type { RegistrationUserDataPlugin } from '../plugin-types.js';
import { TudaMentoringTopicEnum, type TudaStammdaten } from '../stammdaten/tuda-stammdaten-plugin.js';

export const tudaRegistrationUserDataPlugin: RegistrationUserDataPlugin<TudaStammdaten> = {
  id: 'registration::user-data::tuda',
  type: 'registration::user-data',
  render: (
    stContr: GenericFieldFormController<TudaStammdaten>,
    translationController: TranslationController<BHTranslationDictionary>,
  ) => {
    const genericFieldOption = (fieldName: string) =>
      ({
        placeholder: translationController.translate('tuda', 'field', fieldName as any) as string,
        placeholderMode: 'static-floating',
      } as const);

    // helper
    const enumKeys = (ennuum: any) => Object.keys(ennuum).filter(key => !key.startsWith('_'));

    // components
    const textarea = (key: Parameters<typeof stContr.field>[0]) => html`
      <x-textarea
        placeholder=${translationController.translate('tuda', 'field', key as any) + '*'}
        ${stContr.field(key)}
      ></x-textarea>
    `;

    const countryPicker = (key: Parameters<typeof stContr.field>[0]) => html`
      <x-country-picker
        showflag
        language=${globalLanguageSubject.getValue()}
        placeholder=${translationController.translate('tuda', 'field', key as any) + '*'}
        ${stContr.field(key)}
      >
      </x-country-picker>
    `;

    const multiselect = (key: Parameters<typeof stContr.field>[0], selectables: string[]) => html`
      <tr-dropdown-multiselection
        ${stContr.field(key)}
        placeholder=${translationController.translate('tuda', 'field', key as any) + '*'}
        .selectables=${selectables}
        .renderString=${key => translationController.translate('enums', key)}
      ></tr-dropdown-multiselection>
    `;

    return html`
      <x-flex space="lg">
        <!-- personal data -->
        <x-flex space="sm">
          ${stContr.genericField(['gender'], genericFieldOption('gender'))}
          <x-date-picker-dropdown
            placeholder=${translationController.translate('tuda', 'field', 'dateOfBirth') + '*'}
            ${stContr.field('dateOfBirth')}
          ></x-date-picker-dropdown>
          <!-- -->
          ${countryPicker('nationality')}
          <!-- -->
          ${countryPicker('countryOfBirth')}
          <x-tel-input
            placeholder=${translationController.translate('tuda', 'field', 'phoneNumber') + '*'}
            ${stContr.field('phoneNumber')}
          ></x-tel-input>

          <!-- addresse -->
          <x-flex space="md">
            <tr-h4> Adresse </tr-h4>
            <x-flex space="sm">
              <x-flex horizontal space="sm" ${acss({ '*:last-child': { flex: '1' } })}>
                ${stContr.genericField(['address', 'zipCity', 'zip'], genericFieldOption('zip'))}
                ${stContr.genericField(['address', 'zipCity', 'city'], genericFieldOption('city'))}
              </x-flex>
            </x-flex>

            ${stContr.genericField(['isImmigrant'], genericFieldOption('isImmigrant'))}
          </x-flex>

          <!-- studium -->
          <x-flex space="sm">
            <tr-h4> Studium </tr-h4>
            ${stContr.genericField(['currentUni'], genericFieldOption('currentUni'))}
            ${stContr.genericField(['semester'], genericFieldOption('semester'))}
            ${stContr.genericField(['courseOfStudy'], genericFieldOption('courseOfStudy'))}
            ${stContr.genericField(['department'], genericFieldOption('department'))}
            ${stContr.genericField(['desiredDegree'], genericFieldOption('desiredDegree'))}
          </x-flex>

          <!-- Herausforderungen und Motivation -->
          <x-flex space="sm">
            <tr-h4> Herausforderungen und Motivation </tr-h4>
            ${textarea('jobHuntingDifficulties')}
            <!-- -->
            ${textarea('participationReason')}
            <!-- -->
            ${textarea('questions')}
          </x-flex>

          <!-- Wünsche an das Programm -->
          <x-flex space="sm">
            <tr-h4> Wünsche an das Programm </tr-h4>
            ${stContr.genericField(['mentorExchangeFrequency'], genericFieldOption('mentorExchangeFrequency'))}
            ${stContr.genericField(['mentorExchangeMethod'], genericFieldOption('mentorExchangeMethod'))}
            ${stContr.genericField(['mentorExchangeLanguage'], genericFieldOption('mentorExchangeLanguage'))}
            ${stContr.genericField(['mentorMatchGender'], genericFieldOption('mentorMatchGender'))}
            ${multiselect('mentoringTopic', enumKeys(TudaMentoringTopicEnum))}
            ${stContr.document?.mentoringTopic?.includes(TudaMentoringTopicEnum.TUDA_ANDERE)
              ? stContr.genericField(['individualizedTopic'], genericFieldOption('individualizedTopic'))
              : nothing}
            ${textarea('additionalInformation')}
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  },
};
